// Koto for the japanese style (docs/AUDIO_DESIGN.md §12). Every pluck is one silk string rendered as a Karplus–Strong
// string in JS into an AudioBuffer (cached per page) and played by one AudioBufferSource + one gain per note through a
// long paulownia body. The pitch lives on the source's playbackRate: a tension glide at the attack, the oshide press
// (the left hand pushes the string behind its bridge and the pitch rises, then eases back) and yuri (a pressed vibrato).
import { clamp } from '../../engine/core.js';
import { mtof } from '../dsp.js';
import { gain, filter, panner } from '../instruments.js';
import { SCALES, toScale } from './japanese-band.js';

const mod12 = n => ((n % 12) + 12) % 12;

/* ---------------- plucks: one Karplus–Strong string at half the context rate ---------------- */
// y[n] = e[n] for the first lap (n < L), then T(H(y[n − L]))   e = the pluck, H = the loss one-pole (meets the T60 at f0
// and at 2 kHz), T = the tuning allpass. The ivory picks (tsume) strike near the bridge end of the string: a small β and a
// bright pick LP, so the attack twangs, then the highs die in a quarter second and a round tone rings on.
const CACHE = new Map();                              // `${rate}:${midi}` → AudioBuffer, built once, lazily
export const KOTO_STATS = { built: 0, ms: 0, maxMs: 0 };
const FB = 2000, TWO_PI = Math.PI * 2;
//   pick position β · pick LP (Hz) · noise share · T60 at 2 kHz (s)
const UPPER = { beta: 0.09, lp: 3500, noise: 0.26, tb: 0.26 }, LOW = { beta: 0.14, lp: 2000, noise: 0.2, tb: 0.22 };
const apDelay = (c, w) => 1 - 2 * Math.atan2(c * Math.sin(w), 1 + c * Math.cos(w)) / w;   // 1st-order allpass phase delay
/** T60 of the fundamental: 2.8·(196/f)^0.5 s (G2 3.6 s · C4 2.4 s · G5 1.4 s) */
const t60 = f => clamp(2.8 * Math.pow(196 / f, 0.5), 1.2, 3.6);

function render(rate, midi) {
  const f0 = mtof(midi), R = midi < 52 ? LOW : UPPER, P = rate / f0, w0 = TWO_PI * f0 / rate, wb = TWO_PI * FB / rate, T0 = t60(f0);
  // the buffer lasts 0.55 of the T60 (1.1–2.1 s), then fades
  const N = Math.round(clamp(0.55 * T0, 1.1, 2.1) * rate), y = new Float32Array(N);
  const A0 = Math.pow(10, -3 / (f0 * T0)), want = Math.pow(10, -3 / (f0 * Math.min(R.tb, 0.8 * T0))) / A0;
  const mag = (a, w) => (1 - a) / Math.sqrt(1 - 2 * a * Math.cos(w) + a * a);
  let lo = 0, hi = 0.97;
  for (let k = 0; k < 36; k++) { const m = (lo + hi) / 2; if (mag(m, wb) / mag(m, w0) > want) lo = m; else hi = m; }
  const a = (lo + hi) / 2, b0 = (A0 / mag(a, w0)) * (1 - a);
  const rest = P - Math.atan2(a * Math.sin(w0), 1 - a * Math.cos(w0)) / w0, L = Math.max(2, Math.floor(rest - 0.5)), d = rest - L;
  let cl = -0.9, ch = 0.9;
  for (let k = 0; k < 36; k++) { const m = (cl + ch) / 2; if (apDelay(m, w0) > d) cl = m; else ch = m; }
  const C = (cl + ch) / 2;
  // the pluck: a triangle peaking at β·L + noise combed at the same point, rounded by a periodic one-pole LP run twice,
  // mean removed; the loop filters are primed with that lap so the second lap follows without a step
  const pk = Math.max(1, Math.round(R.beta * L)), e = new Float64Array(L), nz = new Float64Array(L);
  let s0 = (midi * 7919 + 101) >>> 0;
  const rnd = () => { s0 = (Math.imul(s0, 1664525) + 1013904223) >>> 0; return s0 / 4294967296; };
  const al = 1 - Math.exp(-TWO_PI * R.lp / rate);
  for (let i = 0; i < L; i++) nz[i] = rnd() * 2 - 1;
  const nc = Float64Array.from(nz, (v, i) => v - nz[(i - pk + L) % L]);
  let ns = 0;
  for (let i = 0; i < L; i++) ns += nc[i] * nc[i];
  const nk = R.noise * 0.3 / (Math.sqrt(ns / L) || 1);
  for (let i = 0; i < L; i++) e[i] = (1 - R.noise) * (i <= pk ? i / pk : (L - i) / (L - pk)) + nk * nc[i];
  let z = 0, z2 = 0, mean = 0;
  for (let lap = 0; lap < 2; lap++) for (let i = 0; i < L; i++) { z += al * (e[i] - z); z2 += al * (z - z2); if (lap) e[i] = z2; }
  for (let i = 0; i < L; i++) mean += e[i] / L;
  for (let i = 0; i < L; i++) e[i] -= mean;
  let h = 0, tx = 0, ty = 0;
  const loop = x => { h = b0 * x + a * h; const tv = C * h + tx - C * ty; tx = h; ty = tv; return tv; };
  for (let i = 0; i < L; i++) loop(e[i]);
  for (let n = 0; n < N; n++) y[n] = n < L ? e[n] : loop(y[n - L]);
  // the body hears the force at the bridge, the string's slope y[n] − y[n−1] (+6 dB/oct), then a DC blocker (~20 Hz), a
  // 2 ms attack ramp, a 0.22 s end fade, and RMS of 0–0.3 s → 0.2
  const hp = 1 - TWO_PI * 20 / rate, na = Math.max(2, Math.round(0.002 * rate)), nf = Math.round(0.22 * rate), n3 = Math.min(N, Math.round(0.3 * rate));
  let px = 0, py = 0, yl = 0, ss = 0;
  for (let n = 0; n < N; n++) {
    const sl = y[n] - yl; yl = y[n];
    py = sl - px + hp * py; px = sl;
    let v = py;
    if (n < na) v *= 0.5 - 0.5 * Math.cos(Math.PI * n / na);
    if (n >= N - nf) v *= 0.5 + 0.5 * Math.cos(Math.PI * (n - N + nf) / nf);
    y[n] = v;
    if (n < n3) ss += v * v;
  }
  const k = 0.2 / (Math.sqrt(ss / n3) || 1);
  for (let n = 0; n < N; n++) y[n] *= k;
  return y;
}

/** The cached string of one note, rendered on first use. */
export function kotoString(ac, midi) {
  const rate = ac.sampleRate >= 44100 ? Math.round(ac.sampleRate / 2) : ac.sampleRate, key = `${rate}:${midi}`;
  let b = CACHE.get(key);
  if (!b) {
    const t = performance.now(), y = render(rate, midi);
    b = ac.createBuffer(1, y.length, rate);
    b.getChannelData(0).set(y);
    CACHE.set(key, b);
    const ms = performance.now() - t;
    KOTO_STATS.built++; KOTO_STATS.ms += ms; KOTO_STATS.maxMs = Math.max(KOTO_STATS.maxMs, ms);
  }
  return b;
}

/* ---------------- the instrument ---------------- */
// Levels (tuning knobs): the koto against the EP it replaces, the koto lead, the reverb send.
export const KOTO = { level: 11.4, lead: 14, send: 0.3 };
const BANDS = 6;                                     // panners: 6-semitone bands of strings from MIDI 43 (low far left)
const bandOf = m => clamp(Math.floor((m - 43) / 6), 0, BANDS - 1);

/**
 * createKoto(ac, out, send, plan, opts) → { chord, note, pluck, bend, tremolo, warm, stats, dispose }   (the style's `ep`, or a lead)
 * plan.ep.lp: the bus low-pass; plan.japanese: { scale, oshide }. opts: { registry, tag, cap, r, t0, log, stepDur, pan,
 * spread, level, sendLevel, lp }.
 * Bus: 6 string panners (−.28 … +.28 by pitch) → HP 70 Hz → body 190 Hz (+3.5 dB, Q 1) → nasal 1.35 kHz (+2.5 dB, Q 1.3)
 * → twang 3.4 kHz (+1.5 dB, Q 1.2) → high shelf 3 kHz −5 dB (bright, never glassy) → allpass 1.2 kHz (Q .7: the body
 * smears the pick's click, less crest) → LP (Q .6) → out ×11.4, reverb send .3. Per note: A = .16·v^1.25, ±2 ¢; the attack starts 6 + 10v ¢ sharp and settles in
 * τ 35 ms (the tension glide), release τ = the event's rel. Each pitch is its own string: a new pluck on a ringing one
 * damps it (τ 12 ms); over the cap the oldest note goes (10 ms).
 * Chord calls (the engine's picks): one note = a pluck (oshide on notes ≥ .45 s with p oshide: after 120–250 ms the
 * press raises it to the scale's next tone above (≤ 2 semitones, else a whole tone), τ 50 ms, holds 180–350 ms, eases
 * back τ 80 ms); two notes = awase (an octave, together) or kaki (a flam: the lower, then the upper 30–45 ms later at
 * .85); three or more = a roll across the strings over `strum` (35–60 ms), the top +6 %; a note picked twice is played
 * in tremolo (sukui: 32nds, 70–130 ms apart, down .9 / up .7, swelling ×.85–1 over the note). Held chords (intro, break,
 * outro) roll up on 1 and play the bass + top together on 3, the next string down on the 8th after; the final chord rolls
 * 110 ms a string and its top gets yuri (±18 ¢ at 5 Hz, in over .5 s), bass + top again a bar later.
 */
export function createKoto(ac, out, send, plan, { registry, tag, cap = 16, r = Math.random, t0 = ac.currentTime, log = null,
  stepDur = 0.2, pan = 0, spread = 1, level = KOTO.level, sendLevel = KOTO.send, lp = plan.ep.lp }) {
  const J = plan.japanese, key = plan.key, scale = SCALES[J.scale] || SCALES.major;
  const pans = Array.from({ length: BANDS }, (_, k) => panner(ac, clamp(pan + spread * (-0.28 + (0.56 * k) / (BANDS - 1)), -1, 1)));
  const busIn = gain(ac, 1), hp = filter(ac, 'highpass', 70, 0.7), body = filter(ac, 'peaking', 190, 1, 3.5);
  const nasal = filter(ac, 'peaking', 1350, 1.3, 2.5), twang = filter(ac, 'peaking', 3400, 1.2, 1.5);
  const shelf = filter(ac, 'highshelf', 3000, null, -5), aps = [1200].map(f => filter(ac, 'allpass', f, 0.7)), low = filter(ac, 'lowpass', lp, 0.6);
  const outG = gain(ac, level), sendG = gain(ac, sendLevel);
  for (const q of pans) q.connect(busIn);
  const chain = [busIn, hp, body, nasal, twang, shelf, ...aps, low, outG];
  chain.forEach((n, k) => k && chain[k - 1].connect(n));
  outG.connect(out); outG.connect(sendG); sendG.connect(send);
  const bus = registry.add({ src: [], all: [...pans, ...chain, sendG], start: t0, end: Infinity, tag: `${tag}:p` });
  let voices = [], maxActive = 0, steals = 0, notes = 0, pending = [];

  const damp = (x, t, tau = 0.012) => { registry.stop(x.v, t, tau); x.end = t; if (x.rec) x.rec[1] = Math.max(x.rec[0], Math.min(x.rec[1], t)); };
  /** the scale's next tone above m within 2 semitones (else a whole tone): where an oshide press goes */
  const pressTo = m => { for (let k = 1; k <= 2; k++) if (scale.includes(mod12(m + k - key.tonic))) return k; return 2; };
  /**
   * one pluck; orn: null | 'oshi' | 'yuri'; soft: a tremolo's re-strike (no attack glide, a gentler damp of the string)
   * → the voice record { v, s, m, rate, start, end } (a lead's oshide slide bends it later)
   */
  function pluck(t, midi, vel, dur, relTau = 0.08, orn = null, soft = false) {
    const buf = kotoString(ac, midi);
    voices = voices.filter(x => x.end > t);
    for (const x of voices) if (x.m === midi && x.start <= t + 1e-3) damp(x, t, soft ? 0.025 : 0.012);
    voices = voices.filter(x => x.end > t);
    if (voices.length >= cap) { const old = voices.shift(); registry.stop(old.v, t, 0.01); old.end = t; if (old.rec) old.rec[1] = t; steals++; }
    const rate = Math.pow(2, (r() * 4 - 2) / 1200), s = ac.createBufferSource(), g = gain(ac, 0), pr = s.playbackRate;
    s.buffer = buf;
    if (soft) pr.value = rate;
    else { pr.setValueAtTime(rate * Math.pow(2, (6 + 10 * vel) / 1200), t); pr.setTargetAtTime(rate, t, 0.035); }
    const off = t + Math.max(0.05, dur);
    if (orn === 'oshi') {
      const at = t + 0.12 + 0.13 * r(), up = Math.pow(2, pressTo(midi) / 12), holdS = 0.18 + 0.17 * r();
      pr.setTargetAtTime(rate * up, at, 0.05); pr.setTargetAtTime(rate, Math.min(off, at + holdS), 0.08);
    } else if (orn === 'yuri' && dur > 0.6) {
      const len = Math.min(dur, 2.4) - 0.3, n = Math.ceil(len * 80), c = new Float32Array(n);   // 16 points a cycle
      for (let i = 0; i < n; i++) { const u = i / (n - 1), tt = u * len; c[i] = rate * Math.pow(2, 18 * Math.min(1, tt / 0.5) * Math.sin(TWO_PI * 5 * tt) / 1200); }
      c[0] = rate;
      pr.setValueCurveAtTime(c, t + 0.3, len);
    }
    s.connect(g); g.connect(pans[bandOf(midi)]);
    const A = 0.16 * Math.pow(vel, 1.25);
    g.gain.setValueAtTime(A, t);
    g.gain.setTargetAtTime(0, off, relTau);
    const bufEnd = t + buf.duration / rate, end = Math.min(bufEnd, off + relTau * 7);
    s.start(t); s.stop(end);
    const v = registry.add({ src: [s], all: [s, g], start: t, end, tag, out: g });
    const x = { v, s, m: midi, rate, start: t, end: Math.min(bufEnd, off + relTau * 4), rec: null };
    if (log) { x.rec = [t, x.end]; log.push(x.rec); }
    voices.push(x);
    notes++;
    maxActive = Math.max(maxActive, voices.length);
    return x;
  }
  /** a note picked in tremolo: re-strikes in 32nds (stepDur / 2, 70–130 ms) up to the chord's end, down .9 / up .7,
   *  swelling ×.85–1. The strikes count back from the hit's end on the grid (t + dur + 30 ms: the engine's hits end
   *  30 ms early), so a chord struck again part-way through (the radio tuning in) lands on the same strikes. */
  function tremolo(t, midi, vel, dur, relTau = 0.08) {
    const dt = clamp(stepDur / 2, 0.07, 0.13), end = t + dur + 0.03, n = Math.max(1, Math.floor((end - t) / dt + 1e-6));
    for (let k = n; k >= 1; k--) {
      const tk = Math.max(t, end - k * dt), v = vel * (k % 2 ? 0.7 : 0.9) * (0.85 + 0.15 * Math.sin(Math.PI * k / (n + 1)));
      pluck(tk + (k % 2 ? 0.004 : 0), midi, v, k === 1 ? Math.max(dt, end - 0.03 - tk) : dt * 1.6, relTau, null, k < n);
    }
  }
  /** a single note with the track's oshide odds */
  const single = (t, m, vel, dur, relTau) => pluck(t, m, vel, dur, relTau, dur >= 0.45 && r() < J.oshide ? 'oshi' : null);
  /** held chords (intro, break, outro: rel ≥ .1) as a koto player fills them: a roll up on 1 (40–80 ms a string), bass +
   *  top together on 3 (.7) and the string under the top on the next 8th (.55), a roll again on the next 1, …; the final
   *  chord (rel ≥ .5) is one slow roll (110 ms a string) with yuri on the top, and the bass and the top again a bar
   *  later. The strikes after the first count back from the chord's end on the grid (see tremolo), so a chord struck
   *  again part-way through plays on exactly the strikes it had. */
  function held(t, midis, vel, dur, relTau) {
    const n = midis.length, half = 8 * stepDur, end = t + dur + 0.03, later = x => x > t + 0.05;
    if (relTau >= 0.5) {
      midis.forEach((m, k) => pluck(t + k * 0.11, m, vel * (0.9 + 0.05 * k), dur, relTau, k === n - 1 ? 'yuri' : null));
      const tb = end - 16 * stepDur;
      if (later(tb) && tb < end - 2 * stepDur) {
        pluck(tb, midis[0], vel * 0.6, end - 0.03 - tb, relTau);
        pluck(tb + 0.02, midis[n - 1], vel * 0.5, end - 0.05 - tb, relTau, 'yuri');
      }
      return;
    }
    const gap = clamp(0.35 * stepDur, 0.04, 0.08), roll = (t0, v0, left) => midis.forEach((m, k) => pluck(t0 + k * gap * (1 + (r() - 0.5) * 0.2), m, v0 * (0.9 + 0.04 * k), left - k * gap, relTau));
    roll(t, vel, dur);
    for (let k = 1; k <= 7; k++) {
      const tk = end - k * half, left = end - 0.03 - tk;
      if (!later(tk)) break;
      if (k % 2 === 0) { roll(tk, vel * 0.88, left); continue; }
      pluck(tk, midis[0], vel * 0.7, left, relTau); pluck(tk + 0.006, midis[n - 1], vel * 0.7, left, relTau);
      if (n > 2 && 2 * stepDur < left) pluck(tk + 2 * stepDur, midis[n - 2], vel * 0.55, left - 2 * stepDur, relTau);
    }
  }
  return {
    pluck, tremolo,
    note: single,
    /** bend a ringing voice to `semis` over its pitch at t (τ 50 ms): a lead's oshide slide into the next note */
    bend(x, t, semis) { const pr = x.s.playbackRate; pr.cancelScheduledValues(t); pr.setTargetAtTime(x.rate * Math.pow(2, semis / 12), t, 0.05); },
    chord(t, midis, vel, dur, strum = 0.04, relTau = 0.08) {
      const count = new Map();
      for (const m of midis) count.set(m, (count.get(m) || 0) + 1);
      const uniq = [...count.keys()].sort((x, y) => x - y), trem = uniq.filter(m => count.get(m) > 1), plain = uniq.filter(m => count.get(m) === 1);
      if (!trem.length && relTau >= 0.095 && uniq.length >= 3) held(t, uniq, vel, dur, relTau);
      else {
        for (const m of trem) tremolo(t, m, vel, dur, relTau);
        const n = plain.length;
        if (n === 1) single(t, plain[0], vel, dur, relTau);
        else if (n === 2) {
          const [a, b] = plain, dt = b - a === 12 ? 0.006 : 0.03 + 0.015 * r();   // awase (octave) or a kaki flam
          pluck(t, a, vel, dur, relTau); pluck(t + dt, b, vel * (b - a === 12 ? 0.95 : 0.85), dur - dt, relTau);
        } else plain.forEach((m, k) => {
          const dt = n > 1 ? (k * strum) / (n - 1) : 0, v = vel * (k === n - 1 ? 1.06 : 1) * (1 + (r() * 2 - 1) * 0.05);
          pluck(t + dt, m, Math.min(1, v), dur - dt, relTau);
        });
      }
      if (pending.length) kotoString(ac, pending.shift());
    },
    /** render the strings of these notes: now within `ms` of JS, the rest one per chord call */
    warm(list, ms = 3) {
      const t = performance.now(), seen = new Set();
      for (const m of list) {
        if (seen.has(m)) continue;
        seen.add(m);
        if (performance.now() - t < ms) kotoString(ac, m); else pending.push(m);
      }
    },
    stats: () => ({ maxActive, steals, notes, active: voices.length }),
    dispose(t) { bus.end = t; },
  };
}

/**
 * createKotoLead(ac, out, send, plan, opts) → { note, warm, dispose }: the melody plucked on a second koto (pan lead.pan,
 * a narrow spread, cap 6, bus LP ×1.15, ×14). Notes are mapped into the track's scale. A slurred note 1–2 semitones above
 * a still-ringing one is an oshide slide (the old string pressed up, no new pluck); notes ≥ 1.2 s are played in tremolo
 * (p .3), else notes ≥ .5 s get an oshide press (p 1.5 × oshide) or yuri (≥ .9 s, p .4).
 */
export function createKotoLead(ac, out, send, plan, opts) {
  const J = plan.japanese, r = opts.r || Math.random;
  const k = createKoto(ac, out, send, plan, { ...opts, cap: 6, pan: plan.lead.pan || 0, spread: 0.3, level: KOTO.lead, sendLevel: 0.36, lp: plan.ep.lp * 1.15 });
  let last = null;
  return {
    note(t, midi, vel, dur, glide = false) {
      const m = toScale(midi, plan.key, J.scale), up = last ? m - last.m : 0;
      // (never a string in yuri: a curve on its playbackRate can't take another automation event)
      if (glide && last && !last.yuri && up >= 1 && up <= 2 && last.bent + up <= 2 && last.x.end > t + 0.05) {
        k.bend(last.x, t, last.bent + up);
        last = { ...last, m, bent: last.bent + up };
        return;
      }
      if (dur >= 1.2 && r() < 0.3) { k.tremolo(t, m, vel * 0.9, dur); last = null; return; }
      const orn = dur >= 0.5 && r() < 1.5 * J.oshide ? 'oshi' : dur >= 0.9 && r() < 0.4 ? 'yuri' : null;
      last = { m, x: k.pluck(t, m, vel, dur, 0.1, orn), bent: 0, yuri: orn === 'yuri' };
    },
    warm: notes => k.warm(notes, 2),
    dispose(t) { k.dispose(t); },
  };
}
